// AI Generated Test
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { HistoryHelperService } from '@client/app/blocks/helpers/history.helper.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'changes-history-dashboard-table-change-history',
    standalone: false,
    template: `
        <table class="table table-hover table-fixed">
            <thead>
            <tr>
                <th class="col-12" translate>
                    Crawled at
                </th>
                <th class="text-right col-5">
                    <span class="visible-print-block" translate>Accessibility Issues</span>
                    <span class="hidden-print" translate>Pages with Accessibility Issues</span>
                </th>
                <th class="text-right col-5" *ngIf="showSEO">
                    <span class="visible-print-block" translate>SEO Opportunities</span>
                    <span class="hidden-print" translate>Pages with SEO Opportunities</span>
                </th>
                <th class="text-right col-5" *ngIf="showQA">
                    <span class="visible-print-block" translate>QA Issues</span>
                    <span class="hidden-print" translate>Pages with QA Issues</span>
                </th>
                <th class="text-right col-5" *ngIf="showPolicy">
                    <span class="visible-print-block" translate>Policy Issues</span>
                    <span class="hidden-print" translate>Pages with Policy Issues</span>
                </th>
                <th class="text-right col-5">
                    <span class="visible-print-block" translate>Pages</span>
                    <span class="hidden-print" translate>Pages crawled</span>
                </th>
                <th class="text-right col-5" *ngIf="domain?.scan?.scan_documents">
                    <span class="visible-print-block" translate>Documents</span>
                    <span class="hidden-print" translate>Documents crawled</span>
                </th>
                <th class="text-right col-6">
                    <span class="visible-print-block" translate>Changes</span>
                    <span class="hidden-print" translate>Changes since previous crawl</span>
                </th>
            </tr>
            </thead>
            <tbody>
            <ng-container *ngFor="let story of sortedHistory; let first = first">
                <tr>
                    <td class="text-v-middle">
                        <user-date [twoLines]="true"
                                   [date]="story.post_processing_done_at || story.date">
                        </user-date>
                    </td>
                    <td class="text-v-middle text-right">
                        {{story.pages_with_accessibility_errors_count | monNumber}}
                    </td>
                    <td class="text-v-middle text-right" *ngIf="showSEO">
                        {{story.pages_with_seo_errors_count | monNumber}}
                    </td>
                    <td class="text-v-middle text-right" *ngIf="showQA">
                        {{story.pages_with_notifications_count | monNumber}}
                    </td>
                    <td class="text-v-middle text-right" *ngIf="showPolicy">
                        {{story.pages_with_policy_matches_count | monNumber}}
                    </td>
                    <td class="text-v-middle text-right">
                        {{story.page_count | monNumber}}
                    </td>
                    <td class="text-v-middle text-right" *ngIf="domain?.scan?.scan_documents">
                        {{story.documents_count | monNumber}}
                    </td>
                    <td class="text-v-middle text-right"
                        [ngClass]="getChangeValueColorClass(story)">
                        <span [ngClass]="getChangePercentageIconClass(story)">
                        </span>
                        {{getChangePercentageValue(story.id)/100 | monNumber:'0.[00]%'}}
                    </td>
                </tr>
                <tr *ngIf="story.browserServiceChanged || story.isScanWithV20">
                    <td colspan="8">
                        <div class="browser-service-update-indicator">
                            <a *ngIf="story.browserServiceChanged"
                                class="browser-service-update-indicator-link"
                                [attr.href]="story.browserServiceChanged.infoLink"
                                target="_blank">{{ browserService[story.js_rendering_service] }}</a>
                            <a *ngIf="story.isScanWithV20"
                                class="browser-service-update-indicator-link"
                                [attr.href]="story.isScanWithV20.infoLink"
                                target="_blank">{{ v20Text }}</a>
                        </div>
                    </td>
                </tr>
            </ng-container>
            </tbody>
        </table>
    `,
})
export class ChangeHistoryComponent implements OnInit, OnChanges {
    @Input() history: any;
    @Input() domain: any;

    showSEO: boolean = false;
    showPolicy: boolean = false;
    showQA: boolean = false;
    browserService: any;
    v20Text: string;
    issuesStack: string[];
    sortedHistory: any = [];

    constructor (
        private historyHelperService: HistoryHelperService,
        private activeFeatureService: ActiveFeatureService,
        private translateService: TranslateService,
    ) {
        this.browserService = {
            'browser_service': this.translateService.getString('Legacy Browser Service changed'),
            'puppeteer_service': this.translateService.getString('Puppeteer Browser Service changed'),
        };
        this.v20Text = this.translateService.getString('Puppeteer 20');
        this.issuesStack = ['page_count', 'documents_count'];
    }

    ngOnInit (): void {
        this.showSEO = this.activeFeatureService.isFeatureActivePure('seo_all');
        this.showPolicy = this.activeFeatureService.isFeatureActivePure('policies_all');
        this.showQA = this.activeFeatureService.isFeatureActivePure('qa_module');
    }

    ngOnChanges (): void {
        if (!this.history) {
            return;
        }

        this.sortedHistory = (safeClone(this.history) || []).sort((a: any, b: any) => {
            return b.id - a.id;
        });
    }

    getChangePercentageValue (id: any): any {
        return this.historyHelperService.getChangePercentageValue(id, this.history, this.issuesStack);
    }

    getChangePercentageIconClass (story: any): any {
        const value = this.getChangeValue(story.id);
        return this.historyHelperService.getChangeIconClass(value);
    }

    getChangeValueColorClass (story: any): any {
        const value = this.getChangeValue(story.id);
        return this.historyHelperService.getChangeColorClass(value);
    }

    private getChangeValue (id: any): any {
        return this.historyHelperService.getChangeValue(id, this.history, this.issuesStack);
    }
}
